import React, { useState } from 'react';
import { FileText, Mail, KeyRound, Loader2, ArrowRight, Building2 } from 'lucide-react';
import { api } from '../../api';
import { useAuth } from './AuthContext';

const STEP = { EMAIL: 'email', OTP: 'otp', REGISTER: 'register' };

export default function LoginPage() {
  const { login } = useAuth();
  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.requestOTP(email);
      setStep(STEP.OTP);
      setInfo('En 6-siffrig kod har skickats till din e-post.');
    } catch (err) {
      if (err.message.includes('hittades inte')) {
        setError('');
        setStep(STEP.REGISTER);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await api.verifyOTP(email, code);
      login(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!tenantName.trim()) { setError('Ange ett företagsnamn'); return; }
    setError(''); setLoading(true);
    try {
      await api.register({ email, tenantName });
      setStep(STEP.OTP);
      setInfo('Konto skapat! En inloggningskod har skickats till din e-post.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="bg-blue-600 p-3 rounded-xl shadow-xl">
              <FileText className="text-white" size={28} />
            </div>
            <span className="text-white font-black text-3xl tracking-tighter">iOffer</span>
          </div>
          <p className="text-slate-400">Från mötesanteckning till offert på minuter</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Steg 1: Ange e-post */}
          {step === STEP.EMAIL && (
            <>
              <h2 className="text-2xl font-black mb-2">Logga in</h2>
              <p className="text-slate-500 text-sm mb-6">Ange din e-post så skickar vi en inloggningskod.</p>
              <form onSubmit={handleRequestOTP} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">E-postadress</label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-3.5 text-slate-400" size={18} />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="din@epost.se"
                    />
                  </div>
                </div>
                {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-black flex items-center justify-center gap-2 shadow-lg"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : <><span>Fortsätt</span><ArrowRight size={18} /></>}
                </button>
              </form>
            </>
          )}

          {/* Steg 2: OTP-kod */}
          {step === STEP.OTP && (
            <>
              <h2 className="text-2xl font-black mb-2">Ange din kod</h2>
              {info && <p className="text-blue-700 text-sm bg-blue-50 p-3 rounded-lg mb-4">{info}</p>}
              <p className="text-slate-500 text-sm mb-6">Koden gäller i 10 minuter.</p>
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">6-siffrig kod</label>
                  <div className="relative mt-1">
                    <KeyRound className="absolute left-3 top-3.5 text-slate-400" size={18} />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required
                      value={code}
                      onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-mono text-2xl tracking-widest text-center"
                      placeholder="000000"
                    />
                  </div>
                </div>
                {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-black flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : 'Logga in'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep(STEP.EMAIL); setCode(''); setError(''); setInfo(''); }}
                  className="w-full text-slate-400 text-sm py-2"
                >
                  Fel e-postadress? Börja om
                </button>
              </form>
            </>
          )}

          {/* Steg 3: Registrering (ny användare) */}
          {step === STEP.REGISTER && (
            <>
              <h2 className="text-2xl font-black mb-2">Skapa konto</h2>
              <p className="text-slate-500 text-sm mb-6">
                <span className="font-bold text-slate-700">{email}</span> är inte registrerat. Fyll i ditt företagsnamn för att komma igång.
              </p>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Företagsnamn</label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-3.5 text-slate-400" size={18} />
                    <input
                      type="text"
                      required
                      value={tenantName}
                      onChange={e => setTenantName(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Mitt Företag AB"
                    />
                  </div>
                </div>
                {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-black flex items-center justify-center gap-2 shadow-lg"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : 'Skapa konto & fortsätt'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep(STEP.EMAIL); setError(''); }}
                  className="w-full text-slate-400 text-sm py-2"
                >
                  Tillbaka
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-slate-500 text-xs mt-6">
          Bjuden in av en kollega? Ange din e-post ovan för att aktivera ditt konto.
        </p>
      </div>
    </div>
  );
}
